/**
 * Column order for the Admin → Permissions matrix.
 *
 * The backend registry (GET /api/admin/permissions/registry) lists keys in the
 * order they were added. The matrix instead follows the dashboard picker
 * (config/dashboards.ts): each dashboard's own key, then its `subPermissions`,
 * so reordering DASHBOARDS reorders both surfaces at once.
 */

import type { DashboardEntry } from '../../config/dashboards';
import type { PermissionKey } from '../../hooks/usePermissions';
import type { PermissionDescriptor } from '../../services/permissionsAdminApi';

export function orderPermissionColumns(
  registry: ReadonlyArray<PermissionDescriptor>,
  dashboards: ReadonlyArray<DashboardEntry>,
): PermissionDescriptor[] {
  const byKey = new Map(registry.map((d) => [d.key, d] as const));
  const ordered: PermissionDescriptor[] = [];
  const seen = new Set<PermissionKey>();

  // Skips keys already placed (Reconciliation reuses invoicing.view) and keys
  // the registry doesn't list (the registry is the truth).
  const take = (key: PermissionKey | undefined) => {
    const d = key ? byKey.get(key) : undefined;
    if (!d || seen.has(d.key)) return;
    seen.add(d.key);
    ordered.push(d);
  };
  const keysOf = (e: DashboardEntry) => [e.permission, ...(e.subPermissions ?? [])];

  // Admin stays the last column. Keys no dashboard names (mcp.connect,
  // mcp.write, anything the backend adds later) go just before it.
  const admin = dashboards.filter((e) => e.id === 'admin');
  const adminKeys = new Set(admin.flatMap(keysOf));
  dashboards.filter((e) => e.id !== 'admin').forEach((e) => keysOf(e).forEach(take));
  registry.forEach((d) => { if (!adminKeys.has(d.key)) take(d.key); });
  admin.flatMap(keysOf).forEach(take);

  return ordered;
}
